import React, { useEffect, useState } from 'react';
import { ModalProps } from '@modals/types';
import { VirtualLicenceQuotaAttachmentResource } from 'dy-frontend-http-repository/lib/modules/VirtualLicence/resources';
import Overlay from '@components/Overlay';
import {
    Button,
    Card,
    Divider,
    Elevation,
    FormGroup,
    HTMLSelect,
    InputGroup,
    Intent,
    Tooltip,
} from '@blueprintjs/core';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import DevText from '@components/Text';
import { QuotaMergeMode, QuotaType } from 'dy-frontend-http-repository/lib/data/enums';
import { quotaTypeInformation } from '@data/consts';
import { VirtualLicenceQuotaAttachmentInput } from 'dy-frontend-http-repository/lib/modules/VirtualLicence/inputs';
import { virtualLicencesApi } from '../../store/apis';
import { updateQuotaAttachmentList } from './store/effects';

export interface SetVirtualLicenceQuotaListModalProps {
    virtualLicenceId: ID;
    initialAttachmentList: VirtualLicenceQuotaAttachmentResource[];
}

type Props = ModalProps<SetVirtualLicenceQuotaListModalProps>;
type Attachment = Omit<VirtualLicenceQuotaAttachmentResource, 'value'> & { value: string };

const SetVirtualLicenceQuotaListModal: React.FC<Props> = ({ closeModal, data }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [attachments, setAttachments] = useState<Attachment[] | null>(null);
    const [quotaPickerValue, setQuotaPickerValue] = useState<QuotaType | null>(null);

    useEffect(() => {
        if (!data) {
            handleModalClose();
            return;
        }

        // Skip if already initialized
        if (attachments !== null) {
            return;
        }

        setAttachments(data.initialAttachmentList.map((a) => ({ ...a, value: `${a.value}` })));
    }, [data]);

    const handleModalClose = () => {
        if (isProcessing) {
            return;
        }

        closeModal?.();
    };

    if (!data) {
        handleModalClose();
        return null;
    }

    const handleSubmit = async () => {
        if (attachments === null) {
            return;
        }

        // Create output data
        const nextAttachmentList: VirtualLicenceQuotaAttachmentInput[] = attachments.map((i) => ({
            type: i.type,
            merge_mode: i.merge_mode,
            value: Number(i.value),
        }));

        // Send data
        setIsProcessing(true);
        try {
            await updateQuotaAttachmentList({ virtualLicenceId: data.virtualLicenceId, input: nextAttachmentList });
            virtualLicencesApi.updateQuotaList({ virtualLicenceId: data.virtualLicenceId, quotas: nextAttachmentList });
            closeModal?.();
        } catch (e) {
            // TODO: handle error
            console.error(e);
        } finally {
            setIsProcessing(false);
        }
    };

    const removeQuotaAttachment = (index: number) => {
        if (attachments === null) {
            return;
        }
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const setQuotaAttachmentValue = (value: string, index: number) => {
        if (attachments === null) {
            return;
        }

        let checkedValue = '';
        if (value.length > 0) {
            const normalizedValue = Number(value);
            if (!isNaN(normalizedValue)) {
                // Min value is 0
                checkedValue = normalizedValue < 0 ? '0' : `${normalizedValue}`;
            }
        }

        setAttachments(
            attachments.map((a, i) => {
                if (i !== index) {
                    return a;
                }

                return { ...a, value: checkedValue };
            })
        );
    };

    const setQuotaAttachmentMergeMode = (index: number, mergeMode: QuotaMergeMode) => {
        if (attachments === null) {
            return;
        }

        setAttachments(
            attachments.map((a, i) => {
                if (i !== index) {
                    return a;
                }

                return { ...a, merge_mode: mergeMode };
            })
        );
    };

    const handleAmountInputBlur = (item: Attachment, index: number) => {
        if (item.value.trim().length !== 0) {
            return;
        }

        let value = '1';
        const foundInitialItem = data.initialAttachmentList.find((a) => a.type === item.type);
        if (foundInitialItem) {
            value = `${foundInitialItem.value}`;
        }

        setQuotaAttachmentValue(value, index);
    };

    const renderAttachmentItem = (item: Attachment, index: number) => {
        const readableQuotaType = quotaTypeInformation[item.type] ?? 'Unknown';

        return (
            <Card compact elevation={Elevation.TWO}>
                <Flex justify="space-between">
                    <Flex className="mb-1" align="center">
                        <HTMLSelect
                            className="mr-1"
                            value={item.merge_mode}
                            onChange={(e) => setQuotaAttachmentMergeMode(index, e.target.value as QuotaMergeMode)}
                        >
                            <option value={QuotaMergeMode.SET}>Set</option>
                            <option value={QuotaMergeMode.SUM}>Add</option>
                        </HTMLSelect>
                        <div style={{ width: '100px' }} className="mr-1">
                            <InputGroup
                                placeholder="Amount"
                                value={item.value}
                                onChange={(e) => setQuotaAttachmentValue(e.target.value, index)}
                                onBlur={(e) => handleAmountInputBlur(item, index)}
                            />
                        </div>
                        <DevText>{readableQuotaType}</DevText>
                    </Flex>

                    <div>
                        <Tooltip content="Remove quota rule">
                            <Button icon="cross" onClick={() => removeQuotaAttachment(index)} />
                        </Tooltip>
                    </div>
                </Flex>
            </Card>
        );
    };

    const renderAttachmentList = () => {
        if (attachments === null) {
            return null;
        }

        if (attachments.length === 0) {
            return <DevText muted>No quotas selected</DevText>;
        }

        return attachments.map((attachment, index) => (
            <div key={`quota-${attachment.type}`} className="mb-1">
                {renderAttachmentItem(attachment, index)}
            </div>
        ));
    };

    const handleAddQuotaType = (type: QuotaType) => {
        if (!attachments) {
            return;
        }

        setAttachments([
            ...attachments,
            {
                type: type,
                merge_mode: QuotaMergeMode.SUM,
                value: '1',
            },
        ]);
    };

    const renderQuotaTypePicker = () => {
        if (!attachments) {
            return null;
        }

        const selectedQuotaTypeList = attachments.map((x) => x.type);
        const availableQuotaTypes = Object.keys(quotaTypeInformation).filter((x) => !selectedQuotaTypeList.includes(x));

        return (
            <FormGroup label="Select quota type to add">
                <Flex>
                    <HTMLSelect
                        fill
                        className="mr-1"
                        onChange={(e) => setQuotaPickerValue(e.target.value as QuotaType)}
                        value={String(quotaPickerValue)}
                        disabled={availableQuotaTypes.length === 0}
                    >
                        <option value={undefined}>Select quota</option>
                        {availableQuotaTypes.map((i) => (
                            <option key={`qt-${i}`} value={i}>
                                {quotaTypeInformation[i] ?? 'Unknown'}
                            </option>
                        ))}
                    </HTMLSelect>
                    <Button
                        disabled={quotaPickerValue === null}
                        onClick={() => {
                            if (quotaPickerValue === null) {
                                return;
                            }
                            handleAddQuotaType(quotaPickerValue);
                            setQuotaPickerValue(null);
                        }}
                    >
                        Add
                    </Button>
                </Flex>
            </FormGroup>
        );
    };

    return (
        <Overlay isOpen onClose={handleModalClose}>
            <Card style={{ width: '558px' }}>
                <Flex className="mb-2" align="center" justify="space-between">
                    <Heading type="h4">Customization quota / limitations list</Heading>
                    <Button minimal icon="cross" onClick={closeModal} disabled={isProcessing} />
                </Flex>

                <Divider className="mb-2" />

                <div className="mb-2">{renderQuotaTypePicker()}</div>

                <Divider className="mb-2" />

                <div className="mb-2">{renderAttachmentList()}</div>

                <Divider className="mb-2" />

                <Flex align="center" justify="space-between">
                    <DevText muted>Save quota info?</DevText>
                    <div>
                        <Button minimal onClick={handleModalClose} className="mr-1" disabled={isProcessing}>
                            No, cancel
                        </Button>
                        <Button intent={Intent.PRIMARY} loading={isProcessing} onClick={handleSubmit}>
                            Yes, save data
                        </Button>
                    </div>
                </Flex>
            </Card>
        </Overlay>
    );
};

export default SetVirtualLicenceQuotaListModal;
